import spanishMessages from 'ra-language-spanish';
import common from './common';
import auth from './auth';
import dashboard from './dashboard';
import leads from './leads';
import settings from './settings';
import hotels from './hotels';
import hotelChains from './hotelChains';
import catalogs from './catalogs';
import backups from './backups';

/**
 * `ra-language-spanish` is stuck at v1.0.0 (last updated 2017) and
 * was never refreshed for react-admin v4 / v5. Many keys the modern
 * react-admin chrome needs (`ra.navigation.page_rows_per_page`,
 * `ra.sort.*`, `ra.saved_queries.*`, `ra.configurable.*`, etc.) are
 * absent and surface as raw key strings in the UI.
 *
 * Fill those gaps inline so the package keeps providing the keys it
 * does have (the bulk) while we override the v5 additions in formal
 * Spain Spanish ("usted"). Mirrored against `ra-language-english@5.0.4`.
 */
const v5RaSpanishOverrides = {
  navigation: {
    no_results: 'No se encontraron resultados',
    no_more_results:
      'La página número %{page} está fuera de los límites. Pruebe con la página anterior.',
    page_out_of_boundaries: 'La página número %{page} está fuera de los límites',
    page_out_from_end: 'No se puede navegar después de la última página',
    page_out_from_begin: 'No se puede navegar antes de la página 1',
    page_range_info: '%{offsetBegin}–%{offsetEnd} de %{total}',
    partial_page_range_info: '%{offsetBegin}–%{offsetEnd} de más de %{offsetEnd}',
    current_page: 'Página %{page}',
    page: 'Ir a la página %{page}',
    first: 'Ir a la primera página',
    last: 'Ir a la última página',
    next: 'Ir a la página siguiente',
    previous: 'Ir a la página anterior',
    page_rows_per_page: 'Filas por página:',
    skip_nav: 'Saltar al contenido',
  },
  sort: {
    sort_by: 'Ordenar por %{field} %{order}',
    ASC: 'ascendente',
    DESC: 'descendente',
  },
  saved_queries: {
    label: 'Consultas guardadas',
    query_name: 'Nombre de la consulta',
    new_label: 'Guardar consulta actual…',
    new_dialog_title: 'Guardar la consulta actual como',
    remove_label: 'Eliminar consulta guardada',
    remove_label_with_name: 'Eliminar la consulta «%{name}»',
    remove_dialog_title: '¿Eliminar la consulta guardada?',
    remove_message: '¿Seguro que desea eliminar este filtro de su lista de consultas guardadas?',
    help: 'Filtre la lista y guarde esta consulta para más tarde',
  },
  configurable: {
    customize: 'Personalizar',
    configureMode: 'Configurar esta página',
    inspector: {
      title: 'Inspector',
      content: 'Pase el ratón sobre los elementos de la aplicación para configurarlos',
      reset: 'Restablecer ajustes',
      hideAll: 'Ocultar todo',
      showAll: 'Mostrar todo',
    },
    Datagrid: {
      title: 'Cuadrícula de datos',
      unlabeled: 'Columna sin etiqueta #%{column}',
    },
    SimpleForm: {
      title: 'Formulario',
      unlabeled: 'Campo sin etiqueta #%{input}',
    },
    SimpleList: {
      title: 'Lista simple',
      primaryText: 'Texto principal',
      secondaryText: 'Texto secundario',
      tertiaryText: 'Texto terciario',
    },
  },
  action: {
    cancel: 'Cancelar',
    clear_array_input: 'Vaciar lista',
    clear_input_value: 'Limpiar campo',
    clone: 'Clonar',
    confirm: 'Confirmar',
    create: 'Crear',
    create_item: 'Crear %{item}',
    delete: 'Eliminar',
    edit: 'Editar',
    export: 'Exportar',
    list: 'Listar',
    refresh: 'Actualizar',
    remove_filter: 'Eliminar este filtro',
    remove_all_filters: 'Eliminar todos los filtros',
    remove: 'Eliminar',
    save: 'Guardar',
    search: 'Buscar',
    select_all: 'Seleccionar todo',
    select_all_button: 'Seleccionar todo',
    select_row: 'Seleccionar esta fila',
    show: 'Mostrar',
    sort: 'Ordenar',
    undo: 'Deshacer',
    unselect: 'Deseleccionar',
    expand: 'Expandir',
    close: 'Cerrar',
    close_menu: 'Cerrar menú',
    open_menu: 'Abrir menú',
    update: 'Actualizar',
    move_up: 'Mover arriba',
    move_down: 'Mover abajo',
    open: 'Abrir',
    toggle_theme: 'Cambiar tema claro/oscuro',
    select_columns: 'Columnas',
    update_application: 'Recargar la aplicación',
    add_filter: 'Añadir filtro',
    add: 'Añadir',
    back: 'Volver',
  },
  message: {
    about: 'Acerca de',
    are_you_sure: '¿Está seguro?',
    auth_error: 'Se ha producido un error al validar el token de autenticación.',
    bulk_delete_content:
      '¿Seguro que desea eliminar este %{name}? |||| ¿Seguro que desea eliminar estos %{smart_count} elementos?',
    bulk_delete_title:
      'Eliminar %{name} |||| Eliminar %{smart_count} %{name}',
    bulk_update_content:
      '¿Seguro que desea actualizar este %{name}? |||| ¿Seguro que desea actualizar estos %{smart_count} elementos?',
    bulk_update_title:
      'Actualizar %{name} |||| Actualizar %{smart_count} %{name}',
    clear_array_input: '¿Seguro que desea vaciar toda la lista?',
    delete_content: '¿Seguro que desea eliminar este elemento?',
    delete_title: 'Eliminar %{name} #%{id}',
    details: 'Detalles',
    error: 'Se ha producido un error en el cliente y su solicitud no se ha podido completar.',
    invalid_form: 'El formulario no es válido. Compruebe si hay errores.',
    loading: 'Cargando, espere por favor.',
    no: 'No',
    not_found: 'O bien ha introducido una URL incorrecta o ha seguido un enlace defectuoso.',
    yes: 'Sí',
    unsaved_changes: 'Algunos de sus cambios no se han guardado. ¿Seguro que desea ignorarlos?',
  },
  input: {
    file: {
      upload_several: 'Arrastre algunos archivos hasta aquí o haga clic para seleccionar uno.',
      upload_single: 'Arrastre un archivo hasta aquí o haga clic para seleccionarlo.',
    },
    image: {
      upload_several: 'Arrastre algunas imágenes hasta aquí o haga clic para seleccionar una.',
      upload_single: 'Arrastre una imagen hasta aquí o haga clic para seleccionarla.',
    },
    references: {
      all_missing: 'No se han podido encontrar las referencias.',
      many_missing: 'Faltan al menos una de las referencias asociadas.',
      single_missing: 'Falta una referencia asociada.',
    },
    password: {
      toggle_visible: 'Ocultar contraseña',
      toggle_hidden: 'Mostrar contraseña',
    },
  },
  page: {
    create: 'Crear %{name}',
    dashboard: 'Panel de control',
    edit: '%{name} #%{recordRepresentation}',
    error: 'Se ha producido un error',
    list: '%{name}',
    loading: 'Cargando',
    not_found: 'No encontrado',
    show: '%{name} #%{recordRepresentation}',
    empty: 'Aún no hay %{name}.',
    invite: '¿Desea añadir uno?',
    access_denied: 'Acceso denegado',
    authentication_error: 'No se puede autenticar la solicitud',
  },
  notification: {
    updated: 'Elemento actualizado |||| %{smart_count} elementos actualizados',
    created: 'Elemento creado',
    deleted: 'Elemento eliminado |||| %{smart_count} elementos eliminados',
    bad_item: 'Elemento incorrecto',
    item_doesnt_exist: 'El elemento no existe',
    http_error: 'Error de comunicación con el servidor',
    data_provider_error: 'Error del proveedor de datos. Compruebe la consola para más detalles.',
    i18n_error: 'No se han podido cargar las traducciones para el idioma indicado',
    canceled: 'Acción cancelada',
    logged_out: 'Su sesión ha terminado, por favor vuelva a iniciar sesión.',
    not_authorized: 'No tiene permisos para acceder a este recurso.',
    application_update_available: 'Hay una nueva versión disponible.',
  },
};

const es = {
  ...spanishMessages,
  ra: {
    ...spanishMessages.ra,
    ...v5RaSpanishOverrides,
    page: {
      ...spanishMessages.ra.page,
      ...v5RaSpanishOverrides.page,
      dashboard: 'Panel de Control',
    },
  },
  common,
  auth,
  dashboard,
  fields: {
    name: 'Nombre',
    description: 'Descripcion',
    status: 'Estado',
    email: 'Email',
    createdAt: 'Fecha de creacion',
    updatedAt: 'Ultima actualizacion',
  },
  resources: {
    leads,
    settings,
    hotels,
    'hotel-chains': hotelChains,
  },
  catalogs,
  backups,
  user: {
    profile: 'Perfil',
    settings: 'Configuracion',
    change_password: 'Cambiar Contrasena',
  },
  pages: {
    login: {
      subtitle: 'Inicia sesion para empezar',
      login_button: 'Iniciar Sesion',
      forgot_password: 'Olvidaste tu contrasena?',
    },
    dashboard: {
      no_data: 'No se encontraron resultados',
      no_data_description: 'No hay datos disponibles en este momento',
    },
  },
  validation: common.validation,
  notifications: common.notifications,
};

export default es;
